import { Plus, X, Crosshair, Wand2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { useOptionSetsByIds } from '@/hooks/useOptionSets'
import { formulaToHuman } from '@/lib/formula'
import { FormFieldRefSelect, type RefFormGroup } from './FormFieldRefSelect'
import { operatorsFor } from './VisibilityBuilder'
import type { FormField } from './types'
import type {
  VisibilityCondition,
  VisibilityGroup,
  VisibilityOperator,
  NotificationRule,
  NotificationChannel,
  NotificationRecipients,
} from '@/types/cgt'
import type {
  NotificationRecipientCandidate,
  NotificationRoleCandidate,
} from '@/api/tenantsApi'

/**
 * Which condition row a canvas click-to-pick is currently armed for.
 *
 * `ruleIdx` because a field now carries several rules: without it, picking a
 * source for rule 2's first condition would write into rule 1's.
 */
export interface NotificationPickTarget {
  ruleIdx: number
  groupIdx: number
  condIdx: number
}

export interface RecipientCandidates {
  members: NotificationRecipientCandidate[]
  superadmins: NotificationRecipientCandidate[]
  roles: NotificationRoleCandidate[]
}

interface Props {
  rule: NotificationRule
  /** This rule's position in the field's list — stamped onto an armed pick. */
  ruleIdx: number
  onChange: (rule: NotificationRule | undefined) => void
  /** Eligible source fields — same list VisibilityBuilder uses. Deliberately
   *  NOT run through the visibility cycle guard: a notification rule doesn't
   *  hide/show anything, so it can't create a visibility cycle, and
   *  self-reference (a field's own value in its own notification condition)
   *  is legal and harmless here, unlike for visibility. */
  candidateFields: FormField[]
  formGroups: RefFormGroup[]
  recipientCandidates: RecipientCandidates | undefined
  recipientsLoading: boolean
  pickArmed: NotificationPickTarget | null
  onArmPick: (target: NotificationPickTarget | null) => void
}

const emptyCondition = (): VisibilityCondition => ({
  sourceFieldId: '',
  operator: 'equals',
  value: '',
})

const defaultRule = (): NotificationRule => ({
  logic: 'and',
  groups: [{ logic: 'and', conditions: [emptyCondition()] }],
  message: '',
  channels: ['on_screen'],
  recipients: { mode: 'all' },
})

const OPERATOR_LABELS: Record<VisibilityOperator, string> = {
  equals: '=',
  notEquals: '≠',
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤',
  contains: 'contains',
  startsWith: 'starts with',
  endsWith: 'ends with',
}

const TEXT_OPERATORS: VisibilityOperator[] = ['contains', 'startsWith', 'endsWith']

/** Tiny AND/OR connector toggle rendered between rows/groups — same as VisibilityBuilder's. */
function ConnectorToggle({
  value,
  onChange,
}: {
  value: 'and' | 'or'
  onChange: (v: 'and' | 'or') => void
}) {
  return (
    <div className='flex justify-center'>
      <div className='inline-flex overflow-hidden rounded-md border text-[10px] font-semibold uppercase'>
        {(['and', 'or'] as const).map(v => (
          <button
            key={v}
            type='button'
            className={cn(
              'px-2 py-0.5 transition-colors',
              value === v
                ? 'bg-primary text-primary-foreground'
                : 'bg-background text-muted-foreground hover:text-foreground',
            )}
            onClick={() => onChange(v)}
          >
            {v}
          </button>
        ))}
      </div>
    </div>
  )
}

/**
 * ONE notification rule — its condition tree, message, channels and audience.
 *
 * Independent of and parallel to VisibilityBuilder's show/hide rule: same
 * two-level AND/OR condition tree, but the action payload is "notify" instead
 * of show/hide. Fires server-side on form-submission completion, so there is
 * no client-side preview of whether it would currently fire.
 *
 * A field holds a LIST of these — see `NotificationRuleBuilder` below, which
 * renders one editor per rule. `onChange(undefined)` means "delete this rule",
 * which is what removing its last condition does.
 */
function NotificationRuleEditor({
  rule,
  ruleIdx,
  onChange,
  candidateFields,
  formGroups,
  recipientCandidates,
  recipientsLoading,
  pickArmed,
  onArmPick,
}: Props) {
  const sourceById = new Map(candidateFields.map(f => [f.id, f]))
  const labelById = Object.fromEntries(
    formGroups.flatMap(g => g.fields.map(f => [f.refKey, f.label])),
  )

  const optionSetIds = [
    ...new Set(
      rule.groups
        .flatMap(g => g.conditions)
        .map(c => sourceById.get(c.sourceFieldId))
        .filter(f => f?.optionsSource === 'optionSet' && f.optionSetId)
        .map(f => f!.optionSetId!),
    ),
  ]
  const optionSets = useOptionSetsByIds(optionSetIds)

  const update = (patch: Partial<NotificationRule>) => onChange({ ...rule, ...patch })

  const updateGroup = (gi: number, patch: Partial<VisibilityGroup>) =>
    update({ groups: rule.groups.map((g, i) => (i === gi ? { ...g, ...patch } : g)) })

  const updateCondition = (gi: number, ci: number, patch: Partial<VisibilityCondition>) =>
    updateGroup(gi, {
      conditions: rule.groups[gi].conditions.map((c, i) =>
        i === ci ? { ...c, ...patch } : c,
      ),
    })

  const setSource = (gi: number, ci: number, sourceFieldId: string) => {
    onArmPick(null)
    const source = sourceById.get(sourceFieldId)
    const current = rule.groups[gi].conditions[ci]
    const validOps = operatorsFor(source)
    updateCondition(gi, ci, {
      sourceFieldId,
      operator: validOps.includes(current.operator) ? current.operator : 'equals',
      value: '',
    })
  }

  const removeCondition = (gi: number, ci: number) => {
    onArmPick(null)
    const groups = rule.groups
      .map((g, i) =>
        i === gi ? { ...g, conditions: g.conditions.filter((_, j) => j !== ci) } : g,
      )
      .filter(g => g.conditions.length > 0)
    if (groups.length === 0) onChange(undefined)
    else update({ groups })
  }

  const toFormulaMode = (gi: number, ci: number) => {
    onArmPick(null)
    updateCondition(gi, ci, { formula: '', sourceFieldId: '', value: '' })
  }
  const toFieldMode = (gi: number, ci: number) => {
    updateCondition(gi, ci, { formula: undefined, sourceFieldId: '', operator: 'equals', value: '' })
  }

  const toggleChannel = (channel: NotificationChannel, checked: boolean) => {
    const channels = checked
      ? [...rule.channels, channel]
      : rule.channels.filter(c => c !== channel)
    update({ channels })
  }

  const setRecipients = (recipients: NotificationRecipients) => update({ recipients })

  const selectedUserIds = new Set(
    rule.recipients.mode === 'members' ? rule.recipients.userIds : [],
  )
  const toggleRecipient = (userId: string, checked: boolean) => {
    const current = rule.recipients.mode === 'members' ? rule.recipients.userIds : []
    const userIds = checked ? [...current, userId] : current.filter(id => id !== userId)
    setRecipients({ mode: 'members', userIds })
  }

  const selectedRoleIds = new Set(rule.recipients.mode === 'roles' ? rule.recipients.roleIds : [])
  const toggleRole = (roleId: string, checked: boolean) => {
    const current = rule.recipients.mode === 'roles' ? rule.recipients.roleIds : []
    const roleIds = checked ? [...current, roleId] : current.filter(id => id !== roleId)
    setRecipients({ mode: 'roles', roleIds })
  }
  // Switching mode starts the new selection EMPTY rather than carrying the old
  // one across: user ids and role ids are different things, and a half-mapped
  // audience is worse than an obviously unfinished one.
  const setMode = (mode: NotificationRecipients['mode']) =>
    setRecipients(
      mode === 'all'
        ? { mode: 'all' }
        : mode === 'roles'
          ? { mode: 'roles', roleIds: [] }
          : { mode: 'members', userIds: [] },
    )

  const valueInput = (gi: number, ci: number, cond: VisibilityCondition) => {
    const source = sourceById.get(cond.sourceFieldId)
    const cls = 'h-8 text-xs'
    if (TEXT_OPERATORS.includes(cond.operator)) {
      return (
        <Input
          className={cls}
          value={String(cond.value)}
          placeholder='Text…'
          onChange={e => updateCondition(gi, ci, { value: e.target.value })}
        />
      )
    }
    if (source?.type === 'select') {
      const options =
        source.optionsSource === 'optionSet' && source.optionSetId
          ? (optionSets.find(s => s.id === source.optionSetId)?.options ?? [])
          : source.options
      if (options.length === 0) {
        return (
          <Input
            className={cls}
            value={String(cond.value)}
            placeholder='Value…'
            onChange={e => updateCondition(gi, ci, { value: e.target.value })}
          />
        )
      }
      return (
        <Select value={String(cond.value)} onValueChange={v => updateCondition(gi, ci, { value: v })}>
          <SelectTrigger className={cls}>
            <SelectValue placeholder='Value…' />
          </SelectTrigger>
          <SelectContent>
            {options.map(o => (
              <SelectItem key={o.id} value={o.label} className='text-xs'>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )
    }
    if (source?.type === 'checkbox' || source?.type === 'switch') {
      return (
        <Select value={String(cond.value)} onValueChange={v => updateCondition(gi, ci, { value: v })}>
          <SelectTrigger className={cls}>
            <SelectValue placeholder='Value…' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='true' className='text-xs'>Checked</SelectItem>
            <SelectItem value='false' className='text-xs'>Unchecked</SelectItem>
          </SelectContent>
        </Select>
      )
    }
    if (source?.type === 'number' || source?.type === 'calculated') {
      return (
        <Input
          type='number'
          className={cls}
          value={String(cond.value)}
          placeholder='Value…'
          onChange={e => {
            const raw = e.target.value
            updateCondition(gi, ci, { value: raw === '' ? '' : Number(raw) })
          }}
        />
      )
    }
    if (source?.type === 'date') {
      return (
        <Input
          type='date'
          className={cls}
          value={String(cond.value)}
          onChange={e => updateCondition(gi, ci, { value: e.target.value })}
        />
      )
    }
    return (
      <Input
        className={cls}
        value={String(cond.value)}
        placeholder='Value…'
        onChange={e => updateCondition(gi, ci, { value: e.target.value })}
      />
    )
  }

  return (
    <div className='space-y-2'>

      {/* Groups — each box is one pair of brackets, same tree as Conditions above */}
      {rule.groups.map((group, gi) => (
        <div key={gi} className='space-y-2'>
          {gi > 0 && <ConnectorToggle value={rule.logic} onChange={v => update({ logic: v })} />}
          <div
            className={cn(
              'space-y-2 rounded-md border p-2',
              rule.groups.length > 1 && 'border-primary/30 bg-primary/[0.03]',
            )}
          >
            {group.conditions.map((cond, ci) => {
              const source = sourceById.get(cond.sourceFieldId)
              // Scoped to THIS rule as well as this row: two rules on one
              // field have a condition at the same coordinates.
              const armed =
                pickArmed?.ruleIdx === ruleIdx &&
                pickArmed?.groupIdx === gi &&
                pickArmed?.condIdx === ci
              const formulaMode = cond.formula !== undefined
              return (
                <div key={ci} className='space-y-1'>
                  {ci > 0 && (
                    <ConnectorToggle value={group.logic} onChange={v => updateGroup(gi, { logic: v })} />
                  )}
                  {formulaMode ? (
                    <div className='space-y-1 rounded-md border bg-muted/20 p-1.5'>
                      <div className='flex items-center gap-1'>
                        <Textarea
                          value={cond.formula ?? ''}
                          onChange={e => updateCondition(gi, ci, { formula: e.target.value })}
                          placeholder='e.g. TODAY() &lt;= {expiration}'
                          className='min-h-14 flex-1 font-mono text-xs'
                        />
                        <div className='flex shrink-0 flex-col gap-1'>
                          <Button
                            size='icon'
                            variant='ghost'
                            className='h-8 w-8 text-muted-foreground'
                            title='Switch back to field/operator/value'
                            onClick={() => toFieldMode(gi, ci)}
                          >
                            <Wand2 className='h-3.5 w-3.5' />
                          </Button>
                          <Button
                            size='icon'
                            variant='ghost'
                            className='h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive'
                            aria-label='Remove condition'
                            onClick={() => removeCondition(gi, ci)}
                          >
                            <X className='h-3.5 w-3.5' />
                          </Button>
                        </div>
                      </div>
                      <FormFieldRefSelect
                        size='sm'
                        groups={formGroups}
                        value=''
                        onChange={v =>
                          updateCondition(gi, ci, {
                            formula: `${cond.formula ?? ''}${cond.formula ? ' ' : ''}{${v}}`,
                          })
                        }
                        formPlaceholder='Pick a form…'
                        fieldPlaceholder='Insert a field…'
                      />
                      {cond.formula?.trim() && (
                        <p className='text-[11px] text-muted-foreground'>
                          <span className='font-medium'>Reads as:</span>{' '}
                          {formulaToHuman(cond.formula, labelById)}
                        </p>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className='flex items-start gap-1'>
                        <FormFieldRefSelect
                          size='sm'
                          groups={formGroups}
                          value={cond.sourceFieldId}
                          onChange={v => setSource(gi, ci, v)}
                          formPlaceholder='Pick a form…'
                          fieldPlaceholder='Pick a field…'
                        />
                        <Button
                          size='icon'
                          variant={armed ? 'default' : 'ghost'}
                          className='h-8 w-8 shrink-0'
                          title={
                            armed
                              ? 'Click a field on the canvas — or click again to cancel'
                              : 'Pick the source by clicking a field on the canvas'
                          }
                          aria-pressed={armed}
                          onClick={() => onArmPick(armed ? null : { ruleIdx, groupIdx: gi, condIdx: ci })}
                        >
                          <Crosshair className='h-3.5 w-3.5' />
                        </Button>
                        <Button
                          size='icon'
                          variant='ghost'
                          className='h-8 w-8 shrink-0 text-muted-foreground'
                          title='Switch to a formula instead'
                          onClick={() => toFormulaMode(gi, ci)}
                        >
                          <Wand2 className='h-3.5 w-3.5' />
                        </Button>
                      </div>
                      <div className='flex items-center gap-1'>
                        <Select
                          value={cond.operator}
                          onValueChange={v => updateCondition(gi, ci, { operator: v as VisibilityOperator })}
                        >
                          <SelectTrigger className='h-8 w-fit min-w-16 shrink-0 gap-1 text-xs'>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {operatorsFor(source).map(op => (
                              <SelectItem key={op} value={op} className='text-xs'>
                                {OPERATOR_LABELS[op]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <div className='flex-1'>{valueInput(gi, ci, cond)}</div>
                        <Button
                          size='icon'
                          variant='ghost'
                          className='h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive'
                          aria-label='Remove condition'
                          onClick={() => removeCondition(gi, ci)}
                        >
                          <X className='h-3.5 w-3.5' />
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )
            })}
            <Button
              variant='ghost'
              size='sm'
              className='h-7 w-full text-xs text-muted-foreground'
              onClick={() => updateGroup(gi, { conditions: [...group.conditions, emptyCondition()] })}
            >
              <Plus className='mr-1 h-3 w-3' />
              Condition
            </Button>
          </div>
        </div>
      ))}

      <Button
        variant='outline'
        size='sm'
        className='h-7 w-full text-xs'
        onClick={() => update({ groups: [...rule.groups, { logic: 'and', conditions: [emptyCondition()] }] })}
      >
        <Plus className='mr-1 h-3 w-3' />
        Group (brackets)
      </Button>

      {/* Message */}
      <div className='space-y-1'>
        <Label className='text-xs font-normal'>Message</Label>
        <Textarea
          value={rule.message}
          onChange={e => update({ message: e.target.value })}
          placeholder='e.g. Result exceeded the threshold — please review.'
          className='min-h-14 text-xs'
        />
      </div>

      {/* Channels */}
      <div className='space-y-1'>
        <Label className='text-xs font-normal'>Send via</Label>
        <div className='flex items-center gap-4'>
          <label className='flex items-center gap-1.5 text-xs'>
            <Checkbox
              checked={rule.channels.includes('on_screen')}
              onCheckedChange={v => toggleChannel('on_screen', v === true)}
            />
            On screen
          </label>
          <label className='flex items-center gap-1.5 text-xs'>
            <Checkbox
              checked={rule.channels.includes('email')}
              onCheckedChange={v => toggleChannel('email', v === true)}
            />
            Email
          </label>
        </div>
        {rule.channels.length === 0 && (
          <p className='text-[11px] text-destructive'>Pick at least one channel.</p>
        )}
      </div>

      {/* Recipients */}
      <div className='space-y-1'>
        <Label className='text-xs font-normal'>Recipients</Label>
        <Select
          value={rule.recipients.mode}
          onValueChange={v => setMode(v as NotificationRecipients['mode'])}
        >
          <SelectTrigger className='h-8 text-xs'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all' className='text-xs'>
              All tenant members and superadmins
            </SelectItem>
            <SelectItem value='roles' className='text-xs'>
              By role
            </SelectItem>
            <SelectItem value='members' className='text-xs'>
              Specific people
            </SelectItem>
          </SelectContent>
        </Select>

        {(rule.recipients.mode === 'roles' || rule.recipients.mode === 'members') && (
          <NotificationRecipientLists
            mode={rule.recipients.mode}
            selectedRoleIds={selectedRoleIds}
            selectedUserIds={selectedUserIds}
            onToggleRole={toggleRole}
            onToggleUser={toggleRecipient}
            candidates={recipientCandidates}
            loading={recipientsLoading}
            rolesHint='Resolved when the form is submitted, so whoever holds the role then is notified.'
          />
        )}
      </div>
    </div>
  )
}

/**
 * The role / people checkbox lists behind a "Specific people" or "By role"
 * recipient choice. Shared by the form-builder rule editor and the LIS result
 * review settings, so the two pickers cannot drift apart.
 */
export function NotificationRecipientLists({
  mode,
  selectedRoleIds,
  selectedUserIds,
  onToggleRole,
  onToggleUser,
  candidates,
  loading,
  rolesHint,
}: {
  mode: 'roles' | 'members'
  selectedRoleIds: Set<string>
  selectedUserIds: Set<string>
  onToggleRole: (roleId: string, checked: boolean) => void
  onToggleUser: (userId: string, checked: boolean) => void
  candidates: RecipientCandidates | undefined
  loading: boolean
  rolesHint?: string
}) {
  return (
    <>
    {mode === 'roles' && (
      <div className='max-h-40 space-y-2 overflow-y-auto rounded-md border p-2'>
        {loading && <p className='text-xs text-muted-foreground'>Loading roles…</p>}
        {!loading && !candidates && (
          <p className='text-xs text-muted-foreground'>Select a tenant to pick roles.</p>
        )}
        {candidates && candidates.roles.length === 0 && (
          <p className='text-xs text-muted-foreground'>No roles defined for this tenant.</p>
        )}
        {candidates?.roles.map(r => (
          <label key={r.id} className='flex items-center gap-1.5 text-xs'>
            <Checkbox
              checked={selectedRoleIds.has(r.id)}
              onCheckedChange={v => onToggleRole(r.id, v === true)}
            />
            <span className='truncate'>{r.name}</span>
            {/* The headcount is the whole reason to show it: a role with
                nobody in it looks identical to a well-chosen one until the
                notification fires and reaches no one. */}
            <span className='shrink-0 text-muted-foreground'>
              {r.memberCount === 0 ? 'no members' : `${r.memberCount} member${r.memberCount === 1 ? '' : 's'}`}
            </span>
          </label>
        ))}
        {selectedRoleIds.size === 0 && (
          <p className='text-[11px] text-destructive'>Pick at least one role.</p>
        )}
        {rolesHint && <p className='text-[10px] text-muted-foreground'>{rolesHint}</p>}
      </div>
    )}

    {mode === 'members' && (
      <div className='max-h-40 space-y-2 overflow-y-auto rounded-md border p-2'>
        {loading && (
          <p className='text-xs text-muted-foreground'>Loading recipients…</p>
        )}
        {!loading && !candidates && (
          <p className='text-xs text-muted-foreground'>Select a tenant to pick recipients.</p>
        )}
        {candidates && (
          <>
            {candidates.members.length > 0 && (
              <div className='space-y-1'>
                <p className='text-[10px] font-semibold uppercase text-muted-foreground'>
                  Tenant members
                </p>
                {candidates.members.map(u => (
                  <label key={u.id} className='flex items-center gap-1.5 text-xs'>
                    <Checkbox
                      checked={selectedUserIds.has(u.id)}
                      onCheckedChange={v => onToggleUser(u.id, v === true)}
                    />
                    {u.fullName ?? u.email}
                  </label>
                ))}
              </div>
            )}
            {candidates.superadmins.length > 0 && (
              <div className='space-y-1'>
                <p className='text-[10px] font-semibold uppercase text-muted-foreground'>
                  Superadmins
                </p>
                {candidates.superadmins.map(u => (
                  <label key={u.id} className='flex items-center gap-1.5 text-xs'>
                    <Checkbox
                      checked={selectedUserIds.has(u.id)}
                      onCheckedChange={v => onToggleUser(u.id, v === true)}
                    />
                    {u.fullName ?? u.email}
                  </label>
                ))}
              </div>
            )}
          </>
        )}
        {selectedUserIds.size === 0 && (
          <p className='text-[11px] text-destructive'>Pick at least one recipient.</p>
        )}
      </div>
    )}
    </>
  )
}

/**
 * Every notification rule on a field, and the controls to add or remove one.
 *
 * A list rather than a single rule because the conditions people notify on are
 * not one condition: an out-of-range result pages QA by email, a borderline one
 * flags the floor on screen, and each needs its own message and audience. Each
 * entry is evaluated independently at submit — one firing says nothing about
 * whether its neighbour does.
 */
export function NotificationRuleBuilder({
  rules,
  onChange,
  candidateFields,
  formGroups,
  recipientCandidates,
  recipientsLoading,
  pickArmed,
  onArmPick,
}: Omit<Props, 'rule' | 'ruleIdx' | 'onChange'> & {
  rules: NotificationRule[]
  onChange: (rules: NotificationRule[]) => void
}) {
  const replace = (idx: number, rule: NotificationRule | undefined) => {
    onArmPick(null)
    onChange(rule ? rules.map((r, i) => (i === idx ? rule : r)) : rules.filter((_, i) => i !== idx))
  }

  return (
    <div className='space-y-3'>
      <Label>Notify on submit</Label>
      <p className='text-xs text-muted-foreground'>
        Sends a notification when a form containing this field is submitted and these
        conditions are met. Independent of the visibility rule above.
      </p>

      {rules.map((rule, idx) => (
        <div key={idx} className='space-y-2 rounded-md border p-2'>
          <div className='flex items-center justify-between'>
            {/* Numbered, because the rules are otherwise told apart only by
                their conditions — and two rules often watch the same field. */}
            <p className='text-[10px] font-semibold uppercase text-muted-foreground'>
              Rule {idx + 1}
            </p>
            <Button
              size='icon'
              variant='ghost'
              className='h-6 w-6 text-muted-foreground hover:text-destructive'
              aria-label={`Remove rule ${idx + 1}`}
              onClick={() => replace(idx, undefined)}
            >
              <X className='h-3.5 w-3.5' />
            </Button>
          </div>
          <NotificationRuleEditor
            rule={rule}
            ruleIdx={idx}
            onChange={r => replace(idx, r)}
            candidateFields={candidateFields}
            formGroups={formGroups}
            recipientCandidates={recipientCandidates}
            recipientsLoading={recipientsLoading}
            pickArmed={pickArmed}
            onArmPick={onArmPick}
          />
        </div>
      ))}

      <Button
        variant='outline'
        size='sm'
        className='w-full'
        onClick={() => onChange([...rules, defaultRule()])}
        disabled={candidateFields.length === 0}
      >
        <Plus className='mr-1.5 h-3.5 w-3.5' />
        {rules.length === 0 ? 'Add notification rule' : 'Add another rule'}
      </Button>
      {rules.length === 0 && (
        <p className='text-xs text-muted-foreground'>
          {candidateFields.length === 0
            ? 'No other value-bearing fields to depend on.'
            : 'Notify people when a condition on this field is met at form submit.'}
        </p>
      )}
    </div>
  )
}
